import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, clear, total, add } = useCart();
  const { user } = useAuth();
  const [phase, setPhase] = useState("cart");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [placing, setPlacing] = useState(false);
  const hasPhysical = items.some((i) => i.category === "physical");

  useEffect(() => {
    if (phase === "checkout" && user) {
      if (!name) setName(user.name || "");
      if (!email) setEmail(user.email || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, user]);

  // Abandoned-cart sync: logged-in fans' coffins are remembered server-side
  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => {
      axios
        .post(`${API}/auth/cart`, { items: items.map((i) => ({ product_id: i.id, qty: i.qty })) }, { withCredentials: true })
        .catch(() => {});
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, user]);

  // Pull-list merge: on login, server-side reserves (pull list) flow into the local coffin
  const mergedRef = useRef(false);
  useEffect(() => {
    if (!user || mergedRef.current) return;
    mergedRef.current = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/cart`, { withCredentials: true });
        const serverItems = r.data.items || [];
        if (!serverItems.length) return;
        const prods = await axios.get(`${API}/products`);
        const pmap = Object.fromEntries(prods.data.map((p) => [p.id, p]));
        let added = 0;
        serverItems.forEach((si) => {
          const p = pmap[si.product_id];
          if (p && !items.find((i) => i.id === p.id)) {
            add(p);
            added += 1;
          }
        });
        if (added > 0) {
          toast.success(`Your pull-list reserve${added > 1 ? "s are" : " is"} waiting in the coffin.`, {
            action: { label: "View Coffin", onClick: () => setOpen(true) },
          });
        }
      } catch {
        /* silent — reserves stay server-side */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const close = () => {
    setOpen(false);
    setTimeout(() => setPhase("cart"), 400);
  };

  const placeOrder = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email, renegade. The ledger demands them.");
      return;
    }
    if (hasPhysical && !address.trim()) {
      toast.error("Physical loot needs a mailing address.");
      return;
    }
    setPlacing(true);
    try {
      const ref = localStorage.getItem("atrocity-ref");
      const r = await axios.post(`${API}/checkout/stripe`, {
        customer_name: name,
        customer_email: email,
        shipping_address: address.trim() || undefined,
        items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
        origin_url: window.location.origin,
        referral_code: ref || undefined,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "The ritual failed. Try again.");
      setPlacing(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-50 bg-void/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="fixed right-0 top-0 z-50 h-full w-full max-w-md bg-pulp border-l-4 border-blooddeep flex flex-col"
          >
            <div className="flex items-center justify-between p-6 border-b-2 border-border">
              <h2 className="font-display text-lg text-paper">
                YOUR <span className="text-crimson">COFFIN</span>
              </h2>
              <button data-testid="cart-close-button" onClick={close} className="text-paper/60 hover:text-crimson transition-colors duration-150" aria-label="Close cart">
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {items.length === 0 ? (
                <div className="text-center pt-20">
                  <p className="font-display text-xl text-paper/40 mb-3">THE COFFIN'S EMPTY</p>
                  <p className="font-type text-sm text-paper/50">Go claim a bad girl or two.</p>
                </div>
              ) : (
                <ul className="space-y-5">
                  {items.map((i) => (
                    <li key={i.id} className="flex gap-4 border border-blooddeep/40 bg-charcoal p-3">
                      <img src={i.cover} alt={i.title} className="w-16 h-24 object-cover border border-black" />
                      <div className="flex-1 min-w-0">
                        <p className="font-impact uppercase text-sm text-paper truncate tracking-wide">{i.title}</p>
                        <p className="font-courier text-[10px] uppercase tracking-widest text-bloodglow mt-0.5">{i.format}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button data-testid={`cart-qty-minus-${i.id}`} onClick={() => setQty(i.id, i.qty - 1)} className="p-1 border border-border text-paper/70 hover:text-bloodglow hover:border-crimson transition-colors duration-150" aria-label="Decrease quantity">
                            <Minus size={13} />
                          </button>
                          <span className="font-impact text-sm text-paper w-6 text-center">{i.qty}</span>
                          <button data-testid={`cart-qty-plus-${i.id}`} onClick={() => setQty(i.id, i.qty + 1)} className="p-1 border border-border text-paper/70 hover:text-bloodglow hover:border-crimson transition-colors duration-150" aria-label="Increase quantity">
                            <Plus size={13} />
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-col items-end justify-between">
                        <button data-testid={`cart-remove-${i.id}`} onClick={() => remove(i.id)} className="text-paper/40 hover:text-crimson transition-colors duration-150" aria-label="Remove item">
                          <Trash2 size={16} />
                        </button>
                        <span className="font-marker text-bone">${(i.price * i.qty).toFixed(2)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {phase !== "done" && items.length > 0 && (
              <div className="border-t-2 border-border p-6 space-y-4">
                {phase === "checkout" && (
                  <div className="space-y-3">
                    <input
                      data-testid="checkout-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Name for the ledger"
                      className="w-full font-courier text-sm bg-void border-2 border-input text-paper px-3 py-2.5 focus:outline-none focus:border-crimson placeholder:text-paper/30"
                    />
                    <input
                      data-testid="checkout-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email for the ledger"
                      className="w-full font-courier text-sm bg-void border-2 border-input text-paper px-3 py-2.5 focus:outline-none focus:border-crimson placeholder:text-paper/30"
                    />
                    {hasPhysical && (
                      <textarea
                        data-testid="checkout-address-input"
                        rows={3}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Mailing address for physical loot"
                        className="w-full font-courier text-sm bg-void border-2 border-input text-paper px-3 py-2.5 focus:outline-none focus:border-crimson placeholder:text-paper/30 resize-none"
                      />
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="font-impact uppercase tracking-widest text-xs text-paper/60">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-marker text-bone text-2xl">${total.toFixed(2)}</span>
                </div>
                {phase === "checkout" && typeof window !== "undefined" && window.localStorage.getItem("atrocity-ref") && (
                  <p data-testid="referral-armed-hint" className="font-courier text-[11px] uppercase tracking-widest text-yell">
                    Blood-oath armed — 10% off if this is your first kill
                  </p>
                )}
                <button
                  data-testid="cart-checkout-button"
                  onClick={phase === "cart" ? () => setPhase("checkout") : placeOrder}
                  disabled={placing}
                  className="w-full font-impact uppercase tracking-wider bg-blooddeep text-bone border-2 border-black px-6 py-4 ink-shadow crimson-glow transition-all duration-150 hover:bg-crimson hover:-translate-y-0.5 active:translate-x-1 active:translate-y-1 disabled:opacity-50"
                >
                  {placing ? "Sealing the pact..." : phase === "cart" ? "Checkout" : "Seal the Pact"}
                </button>
                <p className="font-courier text-[10px] text-paper/35 text-center uppercase tracking-widest">
                  Secure card checkout by Stripe
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
